<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Payroll Management</h1>
            <p class="text-slate-400">W-2 employee payroll — tax withholding and direct deposit. Use Payroll (1099) for contractors.</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="showPayrollModal = true" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              Process Payroll
            </button>
            <button @click="exportPayrollData" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Export
            </button>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Active Employees</p>
            <p class="text-3xl font-bold text-white">{{ payrollStats.activeEmployees }}</p>
            <p class="text-sm text-emerald-400">on payroll</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Payroll</p>
            <p class="text-3xl font-bold text-white">${{ payrollStats.totalPayroll.toLocaleString() }}</p>
            <p class="text-sm text-cyan-400">this month</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Pending Payments</p>
            <p class="text-3xl font-bold text-white">{{ payrollStats.pendingPayments }}</p>
            <p class="text-sm text-amber-400">awaiting</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Processed</p>
            <p class="text-3xl font-bold text-white">{{ payrollStats.processedPayments }}</p>
            <p class="text-sm text-purple-400">this month</p>
          </div>
        </div>
      </div>

      <!-- Payroll Overview -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Employee Payroll List -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-semibold text-white">Employee Payroll</h2>
              <div class="flex items-center space-x-2">
                <input 
                  v-model="searchTerm" 
                  type="text" 
                  placeholder="Search employees..."
                  class="form-input w-48"
                />
                <select v-model="filterStatus" class="form-select">
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>

            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-slate-700/50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Employee</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Position</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Hours</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Rate</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Gross Pay</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-700/50">
                  <tr v-for="employee in filteredEmployees" :key="employee.id" class="hover:bg-slate-700/30">
                    <td class="px-4 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div class="w-8 h-8 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mr-3">
                          <span class="text-xs font-bold text-white">{{ employee.name.charAt(0) }}</span>
                        </div>
                        <div>
                          <div class="text-sm font-medium text-white">{{ employee.name }}</div>
                          <div class="text-sm text-slate-400">{{ employee.email }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-4 whitespace-nowrap text-sm text-slate-300">{{ employee.position }}</td>
                    <td class="px-4 py-4 whitespace-nowrap text-sm text-slate-300">{{ employee.hours }}h</td>
                    <td class="px-4 py-4 whitespace-nowrap text-sm text-slate-300">${{ employee.rate }}/hr</td>
                    <td class="px-4 py-4 whitespace-nowrap text-sm font-medium text-white">${{ employee.grossPay.toLocaleString() }}</td>
                    <td class="px-4 py-4 whitespace-nowrap">
                      <span :class="[
                        'px-2 py-1 rounded-full text-xs font-medium',
                        employee.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                        employee.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-500/20 text-slate-400'
                      ]">
                        {{ employee.status }}
                      </span>
                    </td>
                    <td class="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div class="flex items-center space-x-2">
                        <button @click="viewEmployeeDetails(employee)" class="text-emerald-400 hover:text-emerald-300">
                          View
                        </button>
                        <button @click="processPayment(employee.id)" class="text-cyan-400 hover:text-cyan-300">
                          Pay
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Payroll Summary -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Payroll Summary</h3>
            
            <div class="space-y-4">
              <div class="flex justify-between items-center">
                <span class="text-slate-400">Total Gross Pay</span>
                <span class="text-white font-medium">${{ payrollSummary.totalGross.toLocaleString() }}</span>
              </div>
              
              <div class="flex justify-between items-center">
                <span class="text-slate-400">Taxes</span>
                <span class="text-white font-medium">${{ payrollSummary.taxes.toLocaleString() }}</span>
              </div>
              
              <div class="flex justify-between items-center">
                <span class="text-slate-400">Benefits</span>
                <span class="text-white font-medium">${{ payrollSummary.benefits.toLocaleString() }}</span>
              </div>
              
              <div class="flex justify-between items-center">
                <span class="text-slate-400">Deductions</span>
                <span class="text-white font-medium">${{ payrollSummary.deductions.toLocaleString() }}</span>
              </div>
              
              <div class="border-t border-slate-700/50 pt-4">
                <div class="flex justify-between items-center">
                  <span class="text-lg font-semibold text-white">Net Pay</span>
                  <span class="text-lg font-bold text-emerald-400">${{ payrollSummary.netPay.toLocaleString() }}</span>
                </div>
              </div>
            </div>

            <div class="mt-6 space-y-3">
              <button @click="processAllPayments" class="btn-primary w-full">
                Process All Payments
              </button>
              <button @click="generatePayrollReport" class="btn-secondary w-full">
                Generate Report
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Payroll History -->
      <div class="mt-8">
        <h2 class="text-xl font-semibold text-white mb-6">Recent Payroll History</h2>
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Employee</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Amount</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Method</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Reference</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="payment in recentPayments" :key="payment.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(payment.date) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">{{ payment.employee }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">${{ payment.amount.toLocaleString() }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ payment.method }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      payment.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                      payment.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    ]">
                      {{ payment.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-400">{{ payment.reference }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Process Payroll Modal -->
      <div v-if="showPayrollModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Process Payroll</h3>
          
          <form @submit.prevent="processPayroll" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Pay Period</label>
                <select v-model="payrollData.payPeriod" class="form-select w-full" required>
                  <option value="">Select Pay Period</option>
                  <option value="weekly">Weekly</option>
                  <option value="bi-weekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Pay Date</label>
                <input v-model="payrollData.payDate" type="date" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Payment Method</label>
              <select v-model="payrollData.paymentMethod" class="form-select w-full" required>
                <option value="">Select Payment Method</option>
                <option value="direct-deposit">Direct Deposit</option>
                <option value="check">Check</option>
                <option value="bank-transfer">Bank Transfer</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Notes</label>
              <textarea v-model="payrollData.notes" class="form-input w-full" rows="3"></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showPayrollModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Process Payroll
              </button>
            </div>
          </form>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const userStore = useUserStore()

// Reactive data
const showPayrollModal = ref(false)
const searchTerm = ref('')
const filterStatus = ref('')

const payrollStats = ref({
  activeEmployees: 0,
  totalPayroll: 0,
  pendingPayments: 0,
  processedPayments: 0
})

const payrollSummary = ref({
  totalGross: 0,
  taxes: 0,
  benefits: 0,
  deductions: 0,
  netPay: 0
})

const employees = ref([])
const recentPayments = ref([])

const payrollData = ref({
  payPeriod: '',
  payDate: '',
  paymentMethod: '',
  notes: ''
})

// Computed
const filteredEmployees = computed(() => {
  let filtered = employees.value

  if (searchTerm.value) {
    const search = searchTerm.value.toLowerCase()
    filtered = filtered.filter(emp => 
      emp.name.toLowerCase().includes(search) ||
      emp.email.toLowerCase().includes(search) ||
      emp.position.toLowerCase().includes(search)
    )
  }

  if (filterStatus.value) {
    filtered = filtered.filter(emp => emp.status === filterStatus.value)
  }

  return filtered
})

// Methods
const fetchPayrollStats = async () => {
  try {
    const response = await fetch('/api/company/payroll-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      payrollStats.value = data
    }
  } catch (error) {
    console.error('Error fetching payroll stats:', error)
  }
}

const fetchEmployees = async () => {
  try {
    const response = await fetch('/api/company/payroll-employees', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      employees.value = data.employees || []
    }
  } catch (error) {
    console.error('Error fetching employees:', error)
  }
}

const fetchRecentPayments = async () => {
  try {
    const response = await fetch('/api/company/payroll-history', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      recentPayments.value = data.payments || []
    }
  } catch (error) {
    console.error('Error fetching recent payments:', error)
  }
}

const viewEmployeeDetails = (employee: any) => {
  // Navigate to employee details page
}

const processPayment = async (employeeId: number) => {
  try {
    const response = await fetch('/api/company/process-payment', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ employeeId })
    })
    
    if (response.ok) {
      await fetchEmployees()
      await fetchPayrollStats()
    }
  } catch (error) {
    console.error('Error processing payment:', error)
  }
}

const processAllPayments = async () => {
  try {
    const response = await fetch('/api/company/process-all-payments', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      await fetchEmployees()
      await fetchPayrollStats()
    }
  } catch (error) {
    console.error('Error processing all payments:', error)
  }
}

const processPayroll = async () => {
  try {
    const response = await fetch('/api/company/process-payroll', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payrollData.value)
    })
    
    if (response.ok) {
      showPayrollModal.value = false
      payrollData.value = { payPeriod: '', payDate: '', paymentMethod: '', notes: '' }
      await fetchEmployees()
      await fetchPayrollStats()
    }
  } catch (error) {
    console.error('Error processing payroll:', error)
  }
}

const exportPayrollData = () => {
  const csvContent = [
    ['Employee', 'Position', 'Hours', 'Rate', 'Gross Pay', 'Status'],
    ...filteredEmployees.value.map(emp => [
      emp.name,
      emp.position,
      emp.hours,
      `$${emp.rate}/hr`,
      `$${emp.grossPay.toLocaleString()}`,
      emp.status
    ])
  ].map(row => row.join(',')).join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'payroll-data.csv'
  a.click()
  window.URL.revokeObjectURL(url)
}

const generatePayrollReport = () => {
  // Generate detailed payroll report
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchPayrollStats(),
    fetchEmployees(),
    fetchRecentPayments()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
